import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
 import MapLibreMap, { MapMarker } from '../../../../components/MapLibreMap/MapLibreMap';
import AdminSelect, { SelectOption } from '../../components/AdminSelect';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { Location } from './locationTypes';
import LocationMapTooltip, { LocationMapTooltipPosition } from './components/LocationMapTooltip';
import DataLabelPickerModal from './components/DataLabelPickerModal';

interface LocationMapViewProps {
  locations: Location[];
  locationsWithCoords: Location[];
  focusedLocationId?: string | null;
  showByAttribute: string;
  onShowByAttributeChange: (attr: string) => void;
  dataLabelAttribute: string;
  onDataLabelAttributeChange: (attr: string) => void;
  getColorForAttribute: (loc: Location, attr: string) => string;
  getLabelForAttribute: (loc: Location, attr: string) => string;
  mapMarkers: MapMarker[];
  showByOptions: SelectOption[];
  dataLabelOptions: SelectOption[];
  onMarkerPress: (locationId: string) => void;
}

// Map Legend Component
interface MapLegendProps {
  attribute: string;
  locations: Location[];
  getColor: (loc: Location, attr: string) => string;
}

const MapLegend: React.FC<MapLegendProps> = ({ attribute, locations, getColor }) => {
  const legendItems = useMemo(() => {
    const valueMap = new Map<string, number>();
    locations.forEach(loc => {
      const val = (loc as any)[attribute] ?? 'Unassigned';
      valueMap.set(val, (valueMap.get(val) ?? 0) + 1);
    });
    return Array.from(valueMap.entries()).map(([value, count]) => {
      const sampleLoc = locations.find(l => ((l as any)[attribute] ?? 'Unassigned') === value);
      return {
        label: value || 'Unassigned',
        count,
        color: sampleLoc ? getColor(sampleLoc, attribute) : '#999999',
      };
    }).sort((a, b) => b.count - a.count);
  }, [attribute, locations, getColor]);

  if (legendItems.length === 0) return null;

  return (
    <View style={styles.mapLegend}>
      <Text style={styles.mapLegendTitle}>Legend</Text>
      <View style={styles.mapLegendItems}>
        {legendItems.map((item, idx) => (
          <View key={idx} style={styles.mapLegendItem}>
            <View
              style={[styles.mapLegendColor, { backgroundColor: item.color }]}
            />
            <Text style={styles.mapLegendLabel}>{item.label}</Text>
            <Text style={styles.mapLegendCount}>{item.count}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const LocationMapView: React.FC<LocationMapViewProps> = ({
  locations,
  locationsWithCoords,
  focusedLocationId,
  showByAttribute,
  onShowByAttributeChange,
  dataLabelAttribute,
  onDataLabelAttributeChange,
  getColorForAttribute,
  getLabelForAttribute,
  mapMarkers,
  showByOptions,
  dataLabelOptions,
  onMarkerPress,
}) => {
  const southCanadaCenter = { latitude: 52.5, longitude: -96.0 };
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<LocationMapTooltipPosition | null>(null);
  const [showDataLabelPicker, setShowDataLabelPicker] = useState(false);

  const locationById = useMemo(() => new Map(locations.map(location => [location.id, location])), [locations]);
  const hoveredLocation = hoveredLocationId ? locationById.get(hoveredLocationId) ?? null : null;
  const selectedDataLabel = useMemo(() => (
    dataLabelOptions.find(option => option.value === dataLabelAttribute)?.label ?? 'None'
  ), [dataLabelAttribute, dataLabelOptions]);
  const focusedLocation = useMemo(() => {
    if (!focusedLocationId) return null;
    return locationsWithCoords.find(location => location.id === focusedLocationId) ?? null;
  }, [focusedLocationId, locationsWithCoords]);
  const mapCenter = useMemo(() => {
    if (!focusedLocation) return southCanadaCenter;
    const latitude = Number(focusedLocation.latitude);
    const longitude = Number(focusedLocation.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return southCanadaCenter;
    return { latitude, longitude };
  }, [focusedLocation]);
  const focusedMarkerId = useMemo(() => {
    if (!focusedLocationId) return null;
    return mapMarkers.some(marker => marker.id === focusedLocationId) ? focusedLocationId : null;
  }, [focusedLocationId, mapMarkers]);

  useEffect(() => {
    if (hoveredLocationId && !locationById.has(hoveredLocationId)) {
      setHoveredLocationId(null);
      setTooltipPosition(null);
    }
  }, [hoveredLocationId, locationById]);

  const handleMarkerHover = useCallback((locationId: string, position: LocationMapTooltipPosition) => {
    setHoveredLocationId(locationId);
    setTooltipPosition(position);
  }, []);

  const handleMarkerLeave = useCallback(() => {
    setHoveredLocationId(null);
    setTooltipPosition(null);
  }, []);

  return (
    <View style={styles.mapCard}>
      {/* Map note banner */}
      <View style={styles.mapNote}>
        <Text style={styles.mapNoteText}>
          <Text style={styles.mapNoteBold}>Map uses same filters and search. </Text>
          Click point to open full location record.
        </Text>
        <Text style={styles.mapNoteCount}>{mapMarkers.length} locations</Text>
      </View>

      {/* Map Controls Toolbar */}
      <View style={styles.mapToolbar}>
        <View style={styles.mapToolbarGroup}>
          <Text style={styles.mapToolbarLabel}>Show By:</Text>
          <View style={styles.mapSelectWrapper}>
            <AdminSelect
              value={showByAttribute}
              onChange={onShowByAttributeChange}
              options={showByOptions}
            />
          </View>
        </View>
        <View style={styles.mapToolbarGroup}>
          <Text style={styles.mapToolbarLabel}>Data Labels:</Text>
          <TouchableOpacity
            style={styles.dataLabelButton}
            onPress={() => setShowDataLabelPicker(true)}
            activeOpacity={0.7}
          >
            <View style={styles.dataLabelButtonContent}>
              <Text style={styles.dataLabelButtonValue} numberOfLines={1}>
                {selectedDataLabel}
              </Text>
              <Text style={styles.dataLabelButtonChevron}>▾</Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>

      {/* Legend */}
      <MapLegend
        attribute={showByAttribute}
        locations={locationsWithCoords}
        getColor={getColorForAttribute}
      />

      {/* Map */}
      <MapLibreMap
        latitude={mapCenter.latitude}
        longitude={mapCenter.longitude}
        zoom={focusedMarkerId ? 6 : 3}
        homeLatitude={southCanadaCenter.latitude}
        homeLongitude={southCanadaCenter.longitude}
        homeZoom={3}
        height={560}
        markers={mapMarkers}
        highlightedMarkerId={focusedMarkerId}
        onMarkerPress={onMarkerPress}
        onMarkerHover={handleMarkerHover}
        onMarkerLeave={handleMarkerLeave}
      />
      <LocationMapTooltip
        location={hoveredLocation}
        position={tooltipPosition}
        visible={Boolean(hoveredLocation && tooltipPosition)}
      />

      <DataLabelPickerModal
        visible={showDataLabelPicker}
        value={dataLabelAttribute}
        options={dataLabelOptions}
        onClose={() => setShowDataLabelPicker(false)}
        onApply={onDataLabelAttributeChange}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  mapCard: {
    overflow: 'hidden',
  },

  mapNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#F3F7FD',
    borderBottomWidth: 1,
    borderBottomColor: '#D0D0D0',
  },
  mapNoteText: {
    flex: 1,
    fontSize: 11,
    color: '#616161',
    lineHeight: 16,
  },
  mapNoteBold: {
    fontWeight: '700',
    color: '#01346F',
  },
  mapNoteCount: {
    fontSize: 11,
    fontWeight: '600',
    color: '#034895',
    flexShrink: 0,
  },

  mapToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: AdminColors.bgSurface,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
    flexWrap: 'wrap',
  },
  mapToolbarGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mapToolbarLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
  },
  mapSelectWrapper: {
    minWidth: 160,
  },
  dataLabelButton: {
    minWidth: 240,
    minHeight: 36,
    paddingHorizontal: 10,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    backgroundColor: AdminColors.bgSurface,
  },
  dataLabelButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  dataLabelButtonValue: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    lineHeight: 16,
    flexShrink: 1,
  },
  dataLabelButtonChevron: {
    fontSize: 12,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    lineHeight: 14,
  },

  mapLegend: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#F9F9F9',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  mapLegendTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
    marginBottom: 8,
  },
  mapLegendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  mapLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mapLegendColor: {
    width: 12,
    height: 12,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.2)',
  },
  mapLegendLabel: {
    fontSize: 11,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  mapLegendCount: {
    fontSize: 10,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    marginLeft: 4,
  },
});

export default LocationMapView;
